import { useState } from 'react';
import { Plus, Search, Filter, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { useAssets, useAssetCategories, useAssetMaintenance } from '../hooks';
import { AssetCard, AssetForm, MaintenanceForm } from '../components';
import { useI18n } from '@/shared/i18n';
import { useToast } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import type { Asset, AssetStatus, CreateAssetDTO } from '@yawi/types';

const STATUSES: AssetStatus[] = ['ACTIVE', 'MAINTENANCE', 'RETIRED', 'LOST'];

export const AssetsPage = () => {
  const { t } = useI18n();
  const toast = useToast();
  const ability = useAbility();
  const canManage = ability.can('manage', 'Asset');
  const canCreateMaintenance = ability.can('create', 'AssetMaintenance');

  const { categories } = useAssetCategories();
  const {
    assets,
    pagination,
    loading,
    error,
    params,
    search,
    filterByCategory,
    filterByStatus,
    filterByMaintenanceDue,
    goToPage,
    createAsset,
    refresh,
  } = useAssets();

  const { maintenanceDueAssets, createMaintenance } = useAssetMaintenance();

  const [showForm, setShowForm] = useState(false);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    search(searchTerm);
  };

  const handleCreateAsset = async (data: CreateAssetDTO) => {
    setFormLoading(true);
    try {
      await createAsset(data);
      setShowForm(false);
      toast.success(t('inventory.assetCreated'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateMaintenance = async (data: any) => {
    if (!selectedAsset) return;
    setFormLoading(true);
    try {
      await createMaintenance(selectedAsset.id, data);
      setShowMaintenanceForm(false);
      setSelectedAsset(null);
      toast.success(t('inventory.maintenanceCreated'));
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setFormLoading(false);
    }
  };

  const openMaintenanceForm = (asset: Asset) => {
    setSelectedAsset(asset);
    setShowMaintenanceForm(true);
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">{t('inventory.assets')}</h1>
        {canManage && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('inventory.createAsset')}
          </button>
        )}
      </div>

      {/* Maintenance Due Alert */}
      {maintenanceDueAssets.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <h3 className="font-semibold text-yellow-800">
              {t('inventory.maintenanceDueWarning')} ({maintenanceDueAssets.length})
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {maintenanceDueAssets.slice(0, 5).map((asset) => (
              <button
                key={asset.id}
                onClick={() => canCreateMaintenance && openMaintenanceForm(asset)}
                className="px-3 py-1 bg-white border border-yellow-300 rounded-lg text-sm text-yellow-800 hover:bg-yellow-100 transition-colors"
              >
                {asset.name}
              </button>
            ))}
            {maintenanceDueAssets.length > 5 && (
              <span className="px-3 py-1 text-sm text-yellow-600">
                +{maintenanceDueAssets.length - 5} {t('common.more')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-4 space-y-3">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('inventory.searchAssets')}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 border rounded-lg transition-colors ${
              showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={refresh}
            className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </form>

        {/* Filters */}
        {showFilters && (
          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('inventory.category')}</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => filterByCategory(undefined)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    !params.categoryId ? 'bg-primary-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {t('common.all')}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => filterByCategory(cat.id)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      params.categoryId === cat.id ? 'bg-primary-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('inventory.status')}</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => filterByStatus(undefined)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    !params.status ? 'bg-primary-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {t('common.all')}
                </button>
                {STATUSES.map((status) => (
                  <button
                    key={status}
                    onClick={() => filterByStatus(status)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      params.status === status ? 'bg-primary-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {t(`inventory.status.${status.toLowerCase()}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Maintenance Due Filter */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="maintenanceDue"
                checked={params.maintenanceDue === true}
                onChange={(e) => filterByMaintenanceDue(e.target.checked ? true : undefined)}
                className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="maintenanceDue" className="text-sm text-slate-700">
                {t('inventory.showMaintenanceDueOnly')}
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Assets Grid */}
      {!loading && (
        <div className="grid gap-4 md:grid-cols-2">
          {assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onClick={canCreateMaintenance ? () => openMaintenanceForm(asset) : undefined}
              showActions={canManage}
            />
          ))}

          {assets.length === 0 && (
            <div className="col-span-2 text-center py-12 text-slate-500">
              <p>{t('inventory.noAssets')}</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => goToPage(pagination.page - 1)}
            disabled={!pagination.hasPreviousPage}
            className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            {t('common.previous')}
          </button>
          <span className="px-4 py-1 text-sm text-slate-600">
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            onClick={() => goToPage(pagination.page + 1)}
            disabled={!pagination.hasNextPage}
            className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Asset Form Modal */}
      {showForm && (
        <AssetForm
          categories={categories}
          onSubmit={handleCreateAsset}
          onCancel={() => setShowForm(false)}
          loading={formLoading}
        />
      )}

      {/* Maintenance Form Modal */}
      {showMaintenanceForm && selectedAsset && (
        <MaintenanceForm
          assetName={selectedAsset.name}
          onSubmit={handleCreateMaintenance}
          onCancel={() => {
            setShowMaintenanceForm(false);
            setSelectedAsset(null);
          }}
          loading={formLoading}
        />
      )}
    </div>
  );
};
